import { normalizeEmail } from "@productlane/api/src/lib/contact"

export const MAX_CONTACT_SUGGESTIONS = 5

interface ThreadParticipantSource {
	emails?: readonly {
		createdAt: number
		isDeleted?: boolean | null
		from?: unknown
		to?: unknown
		cc?: unknown
	}[]
	slackMessages?: readonly {
		createdAt: number
		isDeleted?: boolean | null
		slackUserId: string
	}[]
	liveChatSession?: {
		messages: readonly {
			createdAt: number
			isDeleted?: boolean | null
			email?: string | null
		}[]
	} | null
}

function addressesOf(value: unknown): string[] {
	if (Array.isArray(value)) return value.flatMap(addressesOf)
	if (value && typeof value === "object" && "email" in value) {
		const email = (value as { email: unknown }).email
		return typeof email === "string" ? [email] : []
	}
	return []
}

// Every address that wrote or was addressed in the thread, most recent first.
export function collectThreadParticipantEmails(
	thread: ThreadParticipantSource,
	slackUsers: readonly { slackId: string; email?: string | null }[],
): string[] {
	const slackEmailById = new Map(
		slackUsers.flatMap((u) => (u.email ? [[u.slackId, u.email]] : [])),
	)
	const entries: { createdAt: number; emails: string[] }[] = []

	for (const email of thread.emails ?? []) {
		if (email.isDeleted) continue
		entries.push({
			createdAt: email.createdAt,
			emails: [email.from, email.to, email.cc].flatMap(addressesOf),
		})
	}
	for (const message of thread.slackMessages ?? []) {
		const email = slackEmailById.get(message.slackUserId)
		if (message.isDeleted || !email) continue
		entries.push({ createdAt: message.createdAt, emails: [email] })
	}
	for (const message of thread.liveChatSession?.messages ?? []) {
		if (message.isDeleted || !message.email) continue
		entries.push({ createdAt: message.createdAt, emails: [message.email] })
	}

	return entries
		.sort((a, b) => b.createdAt - a.createdAt)
		.flatMap((entry) => entry.emails)
}

// Customers and team members apart, so the picker can rank company people
// between them; the workspace's own inboxes never.
export function rankContactSuggestionEmails({
	participantEmails,
	teamEmails,
	excludedEmails,
}: {
	participantEmails: readonly string[]
	teamEmails: readonly string[]
	excludedEmails: readonly string[]
}): { customers: string[]; teammates: string[] } {
	const team = new Set(teamEmails.map(normalizeEmail))
	const excluded = new Set(excludedEmails.map(normalizeEmail))
	const seen = new Set<string>()
	const customers: string[] = []
	const teammates: string[] = []

	for (const raw of participantEmails) {
		const email = normalizeEmail(raw)
		if (!email || seen.has(email) || excluded.has(email)) continue
		seen.add(email)
		;(team.has(email) ? teammates : customers).push(email)
	}

	return { customers, teammates }
}

export function pickSuggestedContacts<T extends { id: string; email: string }>(
	contacts: readonly T[],
	rankedEmails: readonly string[],
	selectedContactId: string | null,
): T[] {
	if (rankedEmails.length === 0) return []
	const rankByEmail = new Map(
		rankedEmails.map((email, index) => [email, index]),
	)
	const matches = new Map<string, { contact: T; rank: number }>()

	for (const contact of contacts) {
		if (contact.id === selectedContactId) continue
		const email = normalizeEmail(contact.email)
		const rank = rankByEmail.get(email)
		if (rank === undefined || matches.has(email)) continue
		matches.set(email, { contact, rank })
	}

	return [...matches.values()]
		.sort((a, b) => a.rank - b.rank)
		.slice(0, MAX_CONTACT_SUGGESTIONS)
		.map((match) => match.contact)
}

// Most threads first.
export function rankCompanyContactIds(
	threads: readonly { contactId?: string | null }[],
): string[] {
	const counts = new Map<string, number>()
	for (const thread of threads) {
		if (!thread.contactId) continue
		counts.set(thread.contactId, (counts.get(thread.contactId) ?? 0) + 1)
	}
	return [...counts.entries()]
		.sort((a, b) => b[1] - a[1])
		.map(([contactId]) => contactId)
}

// The company's most active people, topped up with its other contacts.
export function pickCompanyContacts<
	T extends { id: string; companyId?: string | null },
>(
	contacts: readonly T[],
	{
		companyId,
		rankedContactIds,
		excludedContactIds,
	}: {
		companyId: string | null | undefined
		rankedContactIds: readonly string[]
		excludedContactIds: readonly (string | null)[]
	},
): T[] {
	if (!companyId) return []
	const excluded = new Set(excludedContactIds)
	const rankById = new Map(rankedContactIds.map((id, index) => [id, index]))
	const active: { contact: T; rank: number }[] = []
	const rest: T[] = []

	for (const contact of contacts) {
		if (excluded.has(contact.id)) continue
		const rank = rankById.get(contact.id)
		if (rank !== undefined) active.push({ contact, rank })
		else if (
			contact.companyId === companyId &&
			rest.length < MAX_CONTACT_SUGGESTIONS
		)
			rest.push(contact)
	}

	return [
		...active.sort((a, b) => a.rank - b.rank).map((match) => match.contact),
		...rest,
	].slice(0, MAX_CONTACT_SUGGESTIONS)
}

export function mergeContactSuggestions<T extends { id: string }>(
	...groups: readonly (readonly T[])[]
): T[] {
	const seen = new Set<string>()
	const merged: T[] = []
	for (const contact of groups.flat()) {
		if (seen.has(contact.id)) continue
		seen.add(contact.id)
		merged.push(contact)
		if (merged.length === MAX_CONTACT_SUGGESTIONS) break
	}
	return merged
}
